import {
  FaMapMarkedAlt,
  FaRegClock,
  FaStar,
  FaBinoculars,
  FaBed,
  FaUtensils,
  FaPlaneDeparture
} from "react-icons/fa";
import { IoMdArrowDropright, IoMdCheckmarkCircle } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import banner1 from "../../assets/banner1.jpg";
import banner2 from "../../assets/banner2.jpg";
import banner3 from "../../assets/banner3.jpg";
import banner4 from "../../assets/banner4.jpg";
import banner5 from "../../assets/banner5.jpg";
import banner6 from "../../assets/banner6.jpg";
import banner7 from "../../assets/banner7.jpg";

const hero = {
  kicker: "9 Days Luxury Safari",
  title: "Premium Northern Circuit",
  subtitle: "Arusha • Tarangire • Ngorongoro • Serengeti luxury lodges and game viewing.",
  backgroundImage: banner1,
  meta: ["9 Days", "Private vehicle", "Premium lodges"],
};

const overview = `Safari Highlights
• Spend a night in Arusha and prepare before heading into the parks.
• Game drives in Tarangire National Park, home to large elephant herds and ancient baobabs.
• Cultural experience at Mto wa Mbu, followed by a day inside Ngorongoro Crater.
• Three nights in the Serengeti, timed to coincide with wildlife movements, ending with a return flight to Arusha before international departure back to your country.`;

const highlights = [
  {
    icon: FaBinoculars,
    title: "Tarangire Elephants",
    description: "Large elephant herds and baobab landscapes.",
    color: "#d97706"
  },
  {
    icon: FaStar,
    title: "Ngorongoro Crater",
    description: "Dense Big Five population in natural amphitheater.",
    color: "#f59e0b"
  },
  {
    icon: FaStar,
    title: "Serengeti Migration",
    description: "Three nights tracking wildlife movements.",
    color: "#fbbf24"
  },
];

const details = [
  {
    icon: FaMapMarkedAlt,
    title: "Route",
    description: "Arusha → Tarangire → Ngorongoro → Serengeti → Arusha.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "9 Days/8 Nights premium safari experience.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Arusha Coffee Lodge, Tarangire Treetops, Melia Ngorongoro, Melia Serengeti.",
  },
  {
    icon: FaPlaneDeparture,
    title: "Transfers",
    description: "Private vehicle + optional scenic flights within parks.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Arrive in Arusha via Kilimanjaro International Airport",
    image: banner1,
    body: "On arrival at Kilimanjaro International Airport (JRO), you will be met by our safari representative and assisted through the formalities. A private vehicle will take you to Arusha, where you will check into Arusha Coffee Lodge. Set on the edge of Arusha city in the middle of a working coffee plantation, the lodge combines comfort with an easy sense of place. Guest cottages are located in a coffee farm, and the lodge has inviting spaces to relax, including a leafy courtyard and a welcoming lounge. Later in the day our safari manager will meet you for a pre-safari briefing and to confirm travel details. The remainder of the day is free for rest.",
    activities: "Meals Plan: Bed and Breakfast",
    tags: ["Arrival", "Coffee Lodge", "Briefing"]
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Tarangire National Park",
    image: banner2,
    body: "After early morning breakfast in Arusha, the drive takes you into Tarangire, a park known for its large elephant herds and sweeping landscapes dotted with baobab trees. You arrive in time for lunch at Tarangire Treetops, an exclusive lodge built around towering trees with spacious elevated rooms that look out across the wilderness. In the afternoon, head out for your first game drive, tracking wildlife along the Tarangire River and across open plains where giraffes, zebras, and countless bird species gather. Return in the evening for dinner under the stars, with the lodge's elevated decks giving a sense of connection to the park's wild surroundings.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Elephants", "Baobabs", "Treetops Lodge"]
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Tarangire – Full day",
    image: banner3,
    body: "Spend the day exploring Tarangire's diverse habitats, with a picnic lunch to enjoy in the bush. The park is rich in wildlife, and the seasonal waterholes and swamps often attract elephants, buffalo, and predators. The forests and acacia woodlands are alive with birdlife, making the park rewarding for both keen birders and photographers. You'll have the chance to witness the quieter side of the safari day as animals gather to drink and rest. By evening, return to the lodge for a relaxed dinner and overnight stay.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Full Day Safari", "Birding", "Bush Picnic"]
  },
  {
    dayLabel: "Day 4",
    dayNum: "04",
    title: "Ngorongoro Highlands via Mto wa Mbu",
    image: banner4,
    body: "Leaving Tarangire, traveling towards Ngorongoro Highlands with a cultural stop at Mto wa Mbu. This lively town sits at the base of the Rift Valley escarpment and offers a snapshot of Tanzania's diversity, with over 100 ethnic groups living side by side. A guided walk through the village reveals banana plantations, craft markets, and bustling food stalls before you sit down to a traditional local lunch. Afternoon you continue the ride to the crater's rim where Melia Ngorongoro awaits. Set on the edge of the forest with panoramic views, the lodge combines spacious rooms, attentive service, and a setting that feels close to nature. Dinner is served onsite with the evening left to prepare for tomorrow's crater adventure.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Cultural Visit", "Mto wa Mbu", "Crater Rim"]
  },
  {
    dayLabel: "Day 5",
    dayNum: "05",
    title: "Ngorongoro Crater – Half Day",
    image: banner5,
    body: "This morning you descend into the Ngorongoro Crater, often called Africa's natural amphitheater of wildlife. The floor supports close to 25,000 large animals, including all members of the Big Five: elephant, rhino, lion, leopard, and buffalo. It is also one of the few places in East Africa where black rhino are regularly sighted. The crater, a UNESCO World Heritage Site, is about 260 square kilometers in size with its walls rising 600 meters above the plains. Game viewing is less demanding here compared to other reserves as animals are concentrated within the enclosed ecosystem. After a rewarding morning of sightings, you return to the lodge for lunch and an afternoon at leisure. Dinner is served at the lodge.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Big Five", "Black Rhino", "UNESCO Site"]
  },
  {
    dayLabel: "Day 6",
    dayNum: "06",
    title: "Serengeti National Park via Ngorongoro Conservation Area",
    image: banner6,
    body: "Following breakfast, set out across the Ngorongoro Conservation Area towards the vast Serengeti plains. The drive itself is a highlight, with changing scenery and wildlife sightings along the way. For a quicker option, a short flight from Lake Manyara Airstrip lands you directly in Serengeti's Seronera. Lunch is served at Melia Serengeti Lodge, a refined property that combines panoramic views with contemporary comfort. In the afternoon, head out for a game drive in the central plains or northern corridor, tracking resident wildlife. Return to the lodge for dinner and an overnight stay.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Scenic Drive", "Optional Flight", "Serengeti Arrival"]
  },
  {
    dayLabel: "Day 7&8",
    dayNum: "07-08",
    title: "Serengeti – Full Day",
    image: banner7,
    body: "These two days are solely dedicated to full-day activities in Serengeti, a UNESCO World Heritage Site whose name means 'endless plains' in Maa. Depending on the season, game drives are focused on tracking the Great Migration or following predators across its acacia-dotted golden plains. Wildlife is never scarce here, with lions, cheetahs, leopards, and large herds of antelope among the highlights. On the first morning, you may choose a hot air balloon safari, drifting above the plains before landing for an alfresco breakfast in the bush. Midday is ideal for a relaxed bush lunch, followed by an optional sundowner at day's end. Return to the lodge in the evenings, where the mix of comfort and natural views encourages unwinding after long game drives.",
    activities: "Meal Plan: Breakfast, Lunch, Dinner",
    tags: ["Great Migration", "Balloon Option", "Predators"]
  },
  {
    dayLabel: "Day 9",
    dayNum: "09",
    title: "Fly back to Arusha – transfer to Kilimanjaro International Airport",
    image: banner1,
    body: "After a sumptuous breakfast at the lodge, your driver guide will transfer you to the nearby airstrip for the morning flight back to Arusha. On arrival, you will be met and driven to Arusha Coffee Lodge. You may choose a day room here, which provides a comfortable space to rest and freshen up before your flight. In the afternoon after lunch, your driver will take you to Kilimanjaro International Airport in time for your evening departure. This marks the end of your safari.",
    activities: "Meal Plan: Breakfast and Lunch • End of tour",
    tags: ["Scenic Flight", "Day Room", "Departure"]
  },
];

export default function LuxuryRoute() {
  const appContext = useContext(AppContext);
  
  // Luxury safari theme: Gold, deep navy, cream
  const theme = {
    primary: "#d97706",      // Amber-600 (gold/luxury)
    primaryLight: "#f59e0b", // Amber-500
    accent: "#1e3a5f",       // Deep navy (sophistication)
    bg: "#fffdf5",           // Warm cream (luxury neutral)
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div style={{ 
      maxWidth: "1400px", 
      margin: "0 auto", 
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>
      
      {/* ================= HERO SECTION ================= */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(15, 10, 5, 0.92), rgba(30, 58, 95, 0.82)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem",
          position: "relative"
        }}
      >
        <div style={{ maxWidth: "800px", animation: "fadeInUp 0.8s ease-out" }}>
          <span 
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{ 
              background: "rgba(245,158,11,0.25)", 
              border: "1px solid rgba(245,158,11,0.5)",
              color: "#fcd34d",
              fontSize: "0.9rem",
              fontWeight: "600",
              letterSpacing: "0.5px",
              backdropFilter: "blur(4px)"
            }}
          >
            {hero.kicker}
          </span>
          
          <h1 
            style={{ 
              fontSize: "clamp(2.5rem, 6vw, 4rem)", 
              marginBottom: "1rem", 
              lineHeight: 1.1,
              fontWeight: "700",
              textShadow: "0 2px 15px rgba(0,0,0,0.5)"
            }}
          >
            {hero.title}
          </h1>
          
          <p 
            style={{ 
              fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)", 
              marginBottom: "2.5rem",
              opacity: 0.95,
              lineHeight: 1.6
            }}
          >
            {hero.subtitle}
          </p>
          
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span 
                key={i} 
                style={{ 
                  background: "rgba(255,255,255,0.18)", 
                  padding: "0.5rem 1.25rem", 
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(4px)"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>
        
        {/* ================= OVERVIEW ================= */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 
            style={{ 
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
              marginBottom: "1.5rem", 
              color: theme.text,
              fontWeight: "700"
            }}
          >
            Your Luxury Safari Experience
          </h2>
          <p 
            style={{ 
              fontSize: "1.1rem", 
              lineHeight: 1.8, 
              color: theme.textMuted,
              marginBottom: "2rem",
              whiteSpace: "pre-line"
            }}
          >
            {overview}
          </p>
          
          {/* Quick Stats */}
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {details.map((d, i) => {
              const DIcon = d.icon;
              return (
                <div 
                  key={i}
                  className="col-6 col-md-3"
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                >
                  <DIcon size={22} style={{ color: theme.primary, flexShrink: 0 }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: theme.text }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= HIGHLIGHTS GRID ================= */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Premium Safari Highlights
            </h2>
            <p style={{ color: theme.textMuted }}>Curated experiences for the discerning traveler</p>
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "1.5rem" 
          }}>
            {highlights.map((h, i) => {
              const HIcon = h.icon;
              return (
                <article 
                  key={i} 
                  style={{ 
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "default",
                    border: "1px solid rgba(0,0,0,0.04)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                    e.currentTarget.style.borderColor = h.color + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                  }}
                >
                  {/* Subtle gradient accent */}
                  <div 
                    className="position-absolute top-0 end-0 opacity-10"
                    style={{
                      width: "100px",
                      height: "100px",
                      background: `radial-gradient(circle at top right, ${h.color}33, transparent 70%)`,
                      borderRadius: "0 0 0 100%"
                    }}
                  />
                  
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-2 mb-3 position-relative"
                    style={{
                      width: "52px",
                      height: "52px",
                      background: `linear-gradient(135deg, ${h.color}20, ${h.color}10)`,
                      color: h.color,
                      fontSize: "1.4rem",
                      border: `1px solid ${h.color}40`
                    }}
                  >
                    <HIcon />
                  </div>
                  <h3 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: "600", color: theme.text, position: "relative" }}>
                    {h.title}
                  </h3>
                  <p style={{ color: theme.textMuted, lineHeight: 1.6, fontSize: "0.95rem", margin: 0, position: "relative" }}>
                    {h.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ================= ITINERARY TIMELINE ================= */}
        <section>
          <div className="text-center mb-5">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Your 9-Day Luxury Journey
            </h2>
            <p style={{ color: theme.textMuted }}>Day-by-day itinerary through Tanzania's premier wildlife destinations</p>
          </div>

          <div style={{ position: "relative" }}>
            {/* Timeline connector line (desktop) */}
            <div 
              className="d-none d-lg-block"
              style={{
                position: "absolute",
                left: "50%",
                top: "0",
                bottom: "0",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.primary}33, ${theme.accent}33, transparent)`,
                transform: "translateX(-50%)"
              }}
            />

            {itinerary.map((day, i) => (
              <article 
                key={i} 
                className={`row g-4 align-items-center mb-4 ${i % 2 === 0 ? 'flex-lg-row' : 'flex-lg-row-reverse'}`}
                style={{
                  background: theme.cardBg,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.12)";
                  e.currentTarget.style.borderColor = theme.primary + "66";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.04)";
                }}
              >
                {/* Day Number Badge - Luxury Gold */}
                <div 
                  className="position-absolute top-0 start-0 m-3 rounded-pill px-3 py-1"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
                    color: "#1e293b",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    zIndex: 2,
                    boxShadow: "0 4px 12px rgba(217, 119, 6, 0.4)"
                  }}
                >
                  Day {day.dayNum}
                </div>

                {/* Image Column */}
                <div className="col-lg-5" style={{ position: "relative", minHeight: "280px" }}>
                  <div
                    style={{
                      height: "100%",
                      minHeight: "280px",
                      backgroundImage: `url(${day.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.5s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.03)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                  />
                  {/* Gradient overlay for text readability */}
                  <div 
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(15,10,5,0.75), transparent 60%)"
                    }}
                  />
                </div>

                {/* Content Column */}
                <div className="col-lg-7 p-4 p-lg-5">
                  <h3 
                    style={{ 
                      fontSize: "1.4rem", 
                      marginBottom: "1rem", 
                      fontWeight: "700",
                      color: theme.text,
                      paddingRight: "60px",
                      lineHeight: 1.3
                    }}
                  >
                    {day.title}
                  </h3>
                  
                  <p style={{ lineHeight: 1.7, color: theme.textMuted, marginBottom: "1.25rem", fontSize: "0.95rem" }}>
                    {day.body}
                  </p>
                  
                  {/* Tags */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                    {day.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: `${theme.primary}10`,
                          color: theme.primary,
                          padding: "0.35rem 0.85rem",
                          borderRadius: "50px",
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          border: `1px solid ${theme.primary}25`
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Meals Info - Luxury Badge */}
                  <div 
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.6rem 1.2rem",
                      background: `linear-gradient(135deg, ${theme.accent}15, ${theme.accent}08)`,
                      borderRadius: "10px",
                      fontSize: "0.9rem",
                      color: theme.accent,
                      fontWeight: "500",
                      border: `1px solid ${theme.accent}30`
                    }}
                  >
                    <FaUtensils size={14} />
                    {day.activities}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ================= LUXURY AMENITIES HIGHLIGHT ================= */}
        <section 
          style={{ 
            background: `linear-gradient(135deg, ${theme.accent}08, ${theme.primary}08)`,
            borderRadius: "20px",
            padding: "2.5rem 2rem",
            marginTop: "3rem",
            border: `1px solid ${theme.primary}20`
          }}
        >
          <div className="text-center mb-4">
            <h3 
              style={{ 
                fontSize: "clamp(1.4rem, 3vw, 1.75rem)", 
                fontWeight: "700", 
                marginBottom: "0.75rem",
                color: theme.text
              }}
            >
              Premium Inclusions
            </h3>
          </div>
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {[
              "Private 4x4 Safari Vehicle with Pop-up Roof",
              "Expert English-Speaking Driver-Guide",
              "All Park Fees, Conservation Levies & Taxes",
              "Luxury Lodge Accommodation with Full Board",
              "Scenic Flight Option (Serengeti ↔ Arusha)",
              "24/7 In-Country Support & Concierge Service"
            ].map((item, idx) => (
              <div 
                key={idx}
                className="col-12 col-md-6 col-lg-4"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem"
                }}
              >
                <IoMdCheckmarkCircle size={20} style={{ color: theme.primary, flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontSize: "0.95rem", color: theme.text, lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= CTA SECTION ================= */}
        <section 
          className="text-center mt-6 pt-5"
          style={{ 
            borderTop: `1px solid rgba(0,0,0,0.06)`,
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "3rem 2rem",
            marginTop: "4rem"
          }}
        >
          <FaStar size={42} style={{ color: theme.primary, marginBottom: "1rem" }} />
          <h3 
            style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "700", 
              marginBottom: "1rem",
              color: theme.text
            }}
          >
            Ready for Your Premium Safari?
          </h3>
          <p style={{ color: theme.textMuted, maxWidth: "600px", margin: "0 auto 2rem", lineHeight: 1.6 }}>
            Secure your 9-day luxury Northern Circuit experience. Limited lodge availability for peak migration seasons. Personalized itinerary customization available.
          </p>
          <button 
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{ 
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              fontWeight: "600",
              color: "#1e293b",
              boxShadow: `0 6px 20px ${theme.primary}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onClick={() => appContext.navTo({item:'plan-trip'})}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.primary}40`;
            }}
          >
            Request Custom Quote <IoMdArrowDropright size={20} />
          </button>
          <p style={{ fontSize: "0.85rem", color: theme.textMuted, marginTop: "1.5rem", margin: 0 }}>
            <FaRegClock size={14} className="me-1" /> Response within 24 hours • No obligation consultation
          </p>
        </section>

      </div>

      {/* ================= CSS ANIMATIONS ================= */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 991px) {
          [style*="left: 50%"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}